import { useOrganizationStore } from '../../stores/useOrganizationStore';
import {
  LEGACY_CRM_KEY_BY_APP_KEY,
  RolloutAppKey,
} from '../../utils/rollout';

interface OrgCrm {
  key: string;
  appKey: string;
  credentialId: string;
  connectionId?: string;
}

const KNOWN_APP_KEYS: RolloutAppKey[] = [
  'follow-up-boss',
  'cinc',
  'cinc-pro',
  'brivity',
  'kvcore',
  'sierra',
];

const isConnectedStatus = (status: string | null | undefined) => {
  const normalized = (status ?? '').toLowerCase();
  return normalized === 'active' || normalized === 'connected';
};

export const useOrgCrm = (): { crm: OrgCrm | null; isLoading: boolean } => {
  const organization = useOrganizationStore((s) => s.organization);

  const activeConnection = organization?.crmConnections?.find((c) => isConnectedStatus(c.status));
  if (activeConnection?.app) {
    return {
      crm: {
        key: LEGACY_CRM_KEY_BY_APP_KEY[activeConnection.app as RolloutAppKey] || activeConnection.app,
        appKey: activeConnection.app,
        credentialId: activeConnection.connectionId,
        connectionId: activeConnection.connectionId,
      },
      isLoading: false,
    };
  }

  const rollout = organization?.rollout;
  if (!rollout) return { crm: null, isLoading: false };

  const connectedCred = Array.isArray(rollout.credentials)
    ? rollout.credentials.find((c) => isConnectedStatus(c?.status))
    : undefined;

  if (connectedCred?.app_key) {
    return {
      crm: {
        key: LEGACY_CRM_KEY_BY_APP_KEY[connectedCred.app_key as RolloutAppKey] || connectedCred.app_key,
        appKey: connectedCred.app_key,
        credentialId: (connectedCred.credential_id as string) || '',
      },
      isLoading: false,
    };
  }

  for (const appKey of KNOWN_APP_KEYS) {
    const legacyKey = LEGACY_CRM_KEY_BY_APP_KEY[appKey];
    const legacy = (rollout as Record<string, unknown>)[legacyKey] as
      | { connected?: boolean; credentialId?: string; credential_id?: string }
      | undefined;
    if (legacy?.connected) {
      return {
        crm: {
          key: legacyKey,
          appKey,
          credentialId: legacy.credentialId || legacy.credential_id || '',
        },
        isLoading: false,
      };
    }
  }

  return { crm: null, isLoading: false };
};
